import {
  PopoverPosition as BlueprintPopoverPosition,
  Pre as BlueprintPre,
} from '@blueprintjs/core';
import * as React from 'react';

import { QueryBuilderContext } from 'bones/components/molecules/QueryBuilder/hooks/useQueryBuilder';
import { QueryBuilderModelContext } from 'bones/components/molecules/QueryBuilder/hooks/useQueryBuilderModel';
import { QueryBuilderSchemaContext } from 'bones/components/molecules/QueryBuilder/hooks/useQueryBuilderSchema';
import { TopExpressionBlock } from 'bones/components/molecules/QueryBuilder/TopExpressionBlock';
import { useVoiceboxSchema } from 'bones/components/molecules/Voicebox/context/VoiceboxSchema';
import { useVoiceboxSettings } from 'bones/components/molecules/Voicebox/context/VoiceboxSettings';
import { DEFAULT_MODEL_NAME } from 'bones/constants/model';
import { VoiceboxSettings } from 'bones/constants/voicebox/settings';
import { Link } from 'bones/design-system/components/Link';
import { SystemMessage } from 'bones/design-system/components/notifications/SystemMessage';
import { QueryTokens } from 'bones/design-system/components/QueryTokens';
import { Tooltip } from 'bones/design-system/components/Tooltip';
import { noop } from 'bones/utils/noop';
import { addDraftFields } from 'bones/utils/queryBuilder/helpers/draft';
import { sparql } from 'bones/utils/queryBuilder/parsing/sparql';

export interface VoiceboxQueryBuilderProps {
  messagesRef: React.RefObject<HTMLDivElement>;
  query: string;
  settings: VoiceboxSettings;
  seeQueryTestId: string;
}

export const VoiceboxQueryBuilder: React.VFC<VoiceboxQueryBuilderProps> = ({
  messagesRef,
  query,
  settings,
  seeQueryTestId,
}) => {
  const { copy, queryBuilder } = useVoiceboxSchema();
  const { databasesById, modelsByDatabaseId } = useVoiceboxSettings();

  //  istanbul ignore if
  if (!queryBuilder) {
    return null;
  }

  const model = modelsByDatabaseId[settings.databaseId]?.[
    settings.model || DEFAULT_MODEL_NAME
  ] || { classesByIri: {}, propertiesByIri: {} };
  const results = sparql(
    query,
    model,
    databasesById[settings.databaseId]?.namespaces || {}
  );

  if (!results) {
    return (
      <div className="vb-voicebox-query-builder">
        <BlueprintPre>{query} </BlueprintPre>
      </div>
    );
  }

  const tooltip = (
    <Tooltip
      boundary={messagesRef.current || /* istanbul ignore next */ undefined}
      popoverClassName="vb-voicebox-query-builder-tooltip"
      portalContainer={
        (process.env.NODE_ENV === 'test' &&
          document.getElementById('vet-bones-root')) ||
        /* istanbul ignore next */ undefined
      }
      position={BlueprintPopoverPosition.BOTTOM}
      slot={
        <BlueprintPre>
          <QueryTokens
            allTokens={results.tokens.all}
            ignoredTokens={results.tokens.ignored}
            unprocessedTokens={results.tokens.unprocessed}
          />
        </BlueprintPre>
      }
    >
      <Link onClick={noop} role="button" size="md" testId={seeQueryTestId}>
        {copy.queryBuilder.seeQuery}
      </Link>
    </Tooltip>
  );

  return (
    <div className="vb-voicebox-query-builder">
      <QueryBuilderSchemaContext.Provider value={queryBuilder}>
        <QueryBuilderModelContext.Provider value={model}>
          <QueryBuilderContext.Provider
            value={{ topExpression: addDraftFields(results.topExpression) }}
          >
            <TopExpressionBlock setTopExpression={noop} />
          </QueryBuilderContext.Provider>
        </QueryBuilderModelContext.Provider>
      </QueryBuilderSchemaContext.Provider>
      {results.score === 1 ? (
        tooltip
      ) : (
        <SystemMessage
          text={
            <>
              {copy.queryBuilder.incomplete} ({tooltip})
            </>
          }
          variant="info"
        />
      )}
    </div>
  );
};
